/**
 * Brand copy in one place (docs/REDESIGN.md): card names, the nine worlds,
 * player medallions, reaction stickers and the few lines of flavor text.
 * The asset manifest (public/assets/manifest.json) only supplies pictures.
 */
import type { BotLevel } from '../engine';
import type { Emote } from '../worker/protocol';

export const GAME_NAME = 'Ancient Nine';
export const APP_VERSION = '0.2.0';
export const TAGLINE = 'Golf’s oldest world tour.';
export const SHORT_LINE = 'Nine holes. Eight cards. Lowest score wins.';
export const MOTTO = 'FESTINA LENTE';
export const MAX_OPPONENTS = 5;
export const DEFAULT_OPPONENTS = 2;
export const HOLE_CHOICES = [3, 9, 18] as const;

/**
 * Card names. The asset manifest (public/assets/manifest.json, `cards`) is the
 * source of truth: `name` plus `showName`; an empty name or `showName: false`
 * means the card shows its numeral only. This table is only the fallback until
 * the manifest loads (and for the Worker, which has no manifest of its own).
 */
export const CARD_NAMES: Record<number, string> = {
  [-5]: 'Eureka!',
  0: 'Mummy-gan',
  1: 'Veni, Vidi, Putti',
  2: 'Quetzal Birdie',
  3: 'Putt-hagoras',
  4: '',
  5: '',
  6: '',
  7: '',
  8: '',
  9: '',
  10: 'Sphinx Trap',
  11: 'Sunk to Atlantis',
  12: 'Over the Wall',
};

/** Card names from a manifest's `cards` block; '' for a card that shows no name. */
export function manifestCardNames(raw: unknown): Map<number, string> {
  const out = new Map<number, string>();
  const cards = raw && typeof raw === 'object' ? (raw as { cards?: unknown }).cards : undefined;
  if (!cards || typeof cards !== 'object' || Array.isArray(cards)) return out;
  for (const [k, c] of Object.entries(cards as Record<string, unknown>)) {
    const v = Number(k);
    if (!Number.isInteger(v) || v < -5 || v > 12 || !c || typeof c !== 'object') continue;
    const { name, showName } = c as { name?: unknown; showName?: unknown };
    out.set(v, showName === false || typeof name !== 'string' ? '' : name.trim());
  }
  return out;
}

let liveNames = new Map<number, string>();

/** Install the manifest's names (assets.ts calls this once the manifest loads). */
export function setCardNames(names: Map<number, string>): void {
  liveNames = names;
}

/** A card's current name, or '' when it shows its numeral only. */
export function cardNameOf(value: number): string {
  return liveNames.get(value) ?? CARD_NAMES[value] ?? '';
}

/** Values that carry an illustration; 4 to 9 are plain, typeset cards. */
export const ART_VALUES = new Set([-5, 0, 1, 2, 3, 10, 11, 12]);

export const LEVEL_LABEL: Record<BotLevel, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

// ---------------------------------------------------------------------------
// The nine worlds (REDESIGN.md 5)
// ---------------------------------------------------------------------------

export interface World {
  /** 1..9 */
  n: number;
  name: string;
  place: string;
  flavor: string;
  accent: string;
  /**
   * Tibbs's left and right edges (shell and clubs included) in the phone
   * travel art, as fractions of its width. The portrait crop keeps this span
   * on screen (Travel.tsx, phoneCrop).
   */
  tibbs: readonly [number, number];
}

export const WORLDS: World[] = [
  { n: 1, name: 'Giza Links', place: 'Old Kingdom Egypt', flavor: 'Mind the bunkers. They go on for miles.', accent: '#D9A33B', tibbs: [0.02, 0.41] },
  { n: 2, name: 'The Hanging Gardens', place: 'Babylon, Mesopotamia', flavor: 'Very well watered. Mostly by the rough.', accent: '#2B4C9B', tibbs: [0.4, 0.71] },
  { n: 3, name: 'Olympia', place: 'Classical Greece', flavor: 'Laurels for the winner. Lunch for the rest.', accent: '#4F86C6', tibbs: [0.3, 0.71] },
  { n: 4, name: 'The Colosseum', place: 'Rome', flavor: 'Fifty thousand seats. Every one of them is watching your putt.', accent: '#B8502E', tibbs: [0.05, 0.48] },
  {
    n: 5,
    name: 'Göbekli Tepe',
    place: 'Anatolia',
    flavor: 'The oldest course of all. Eleven thousand years, and still no clubhouse.',
    accent: '#8E7B57',
    tibbs: [0.31, 0.56],
  },
  { n: 6, name: 'The Long Wall', place: 'Han China', flavor: 'The longest dogleg ever built.', accent: '#C0392B', tibbs: [0.44, 0.81] },
  { n: 7, name: 'Fuji Heights', place: 'Japan', flavor: 'Snow on the summit. Wind off the left.', accent: '#D97A93', tibbs: [0.2, 0.52] },
  { n: 8, name: 'Voyagers’ Atoll', place: 'Polynesia', flavor: 'Steered here by the stars. Putts break toward the sea.', accent: '#1F9AA8', tibbs: [0.58, 0.9] },
  { n: 9, name: 'The Cloud Terraces', place: 'Inca Andes', flavor: 'The final hole. Thin air, long carry.', accent: '#7B4F9E', tibbs: [0.32, 0.69] },
];

/** Which world a hole is played at: 1..9 in order, 18 holes go round twice, playoffs use world 9. */
export function worldFor(hole: number, totalHoles: number): World {
  if (hole > totalHoles) return WORLDS[8]!;
  return WORLDS[(Math.max(1, hole) - 1) % 9]!;
}

/** "3/9", or "P1" for the first playoff hole. */
export function holeCounter(hole: number, totalHoles: number): string {
  return hole > totalHoles ? `P${hole - totalHoles}` : `${hole}/${totalHoles}`;
}

// ---------------------------------------------------------------------------
// Player medallions (REDESIGN.md 7.6): a color and a white glyph per seat
// ---------------------------------------------------------------------------

export const PLAYER_COLORS = ['#2B4C9B', '#B8502E', '#2F8F6E', '#D9A33B', '#4F86C6', '#7B4F9E'] as const;
export const PLAYER_GLYPHS = ['sun', 'wave', 'leaf', 'star', 'moon', 'mountain'] as const;
export type Glyph = (typeof PLAYER_GLYPHS)[number];

// ---------------------------------------------------------------------------
// Reactions: Tibbs stickers (REDESIGN.md 4.3). No text appears with them.
// ---------------------------------------------------------------------------

export const STICKER_LABELS: Record<Emote, string> = {
  'well-played': 'Well played',
  oof: 'Oof',
  smug: 'Smug',
  fore: 'Fore!',
};

/** True minus sign for negative numbers. */
export function fmt(n: number): string {
  return n < 0 ? `−${Math.abs(n)}` : String(n);
}

/** The first word of a long name, for narrow tiles. The full name stays in the accessible label. */
export function tileName(name: string, narrow: boolean): string {
  if (!narrow || name.length <= 9) return name;
  const first = name.split(/\s+/)[0] ?? name;
  return first.length >= 2 ? first : name;
}
